import { Injectable } from '@angular/core'
import { Firestore, collection, collectionData, doc, docData, addDoc, updateDoc, deleteDoc, serverTimestamp, query, where, getDocs } from '@angular/fire/firestore'
import { Campaign, campaignsPath } from '@parkupp/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

@Injectable({
    providedIn: 'root',
})
export class CampaignService {
    private collection = collection(this.firestore, campaignsPath())

    constructor(public firestore: Firestore) {}

    /**
     * Get all campaigns
     */
    getAll(): Observable<Campaign[]> {
        return collectionData(this.collection, { idField: '$key' }).pipe(
            map((campaigns: any[]) => campaigns.map((c) => new Campaign(c, c.$key)))
        )
    }

    /**
     * Get campaigns filtered by enterprise key
     * Used by Enterprise Managers who can only access their enterprise's campaigns
     */
    getByEnterprise(enterpriseKey: string): Observable<Campaign[]> {
        const q = query(this.collection, where('denormEnterpriseKey', '==', enterpriseKey))
        return collectionData(q, { idField: '$key' }).pipe(
            map((campaigns: any[]) => campaigns.map((c) => new Campaign(c, c.$key)))
        )
    }

    /**
     * Get a single campaign by key
     */
    get(key: string): Observable<Campaign> {
        const docRef = doc(this.firestore, campaignsPath(), key)
        return docData(docRef, { idField: '$key' }).pipe(map((data: any) => new Campaign(data, data.$key)))
    }

    /**
     * Create a new campaign
     */
    async create(campaign: Campaign): Promise<string> {
        const campaignData: any = {
            ...campaign.parse(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        }
        
        const docRef = await addDoc(this.collection, campaignData)
        return docRef.id
    }

    /**
     * Update an existing campaign
     */
    async update(key: string, campaign: Partial<Campaign>): Promise<void> {
        const docRef = doc(this.firestore, campaignsPath(), key)
        const updateData: any = {
            ...campaign,
            updatedAt: serverTimestamp(),
        }
        // Remove $key if present
        delete updateData.$key
        delete updateData.createdAt // Don't update createdAt
        
        await updateDoc(docRef, updateData)
    }

    /**
     * Delete a campaign
     */
    async delete(key: string): Promise<void> {
        const docRef = doc(this.firestore, campaignsPath(), key)
        await deleteDoc(docRef)
    }

    /**
     * Check if a campaign name is unique
     * @param campaignName The campaign name to check
     * @param excludeKey Optional key to exclude (for edit scenarios)
     */
    async isCampaignNameUnique(campaignName: string, excludeKey?: string): Promise<boolean> {
        const q = query(this.collection, where('campaignName', '==', campaignName))
        const snapshot = await getDocs(q)
        
        if (snapshot.empty) {
            return true
        }
        
        // If we're editing, check if the found document is the one we're editing
        if (excludeKey && snapshot.docs.length === 1 && snapshot.docs[0].id === excludeKey) {
            return true
        }
        
        return false
    }
}




